// ============================================================================
// TIMESHEET ENTRY VIEW
// ============================================================================
// Main weekly timesheet screen. Uses SyutTimesheetGrid internally.
// Follows the enterprise layout pattern with ModuleHeader.
//
// Week starts on WEDNESDAY (Wed, Thu, Fri, Sat, Sun, Mon, Tue).
// Employee submits weekly timesheets, grouped by charge type.
// Each entry is added/edited via the TimesheetEntryPopup.
//
// State flow:
//   - Load timesheet for employee + week via useTimesheetByWeek
//   - Transform API TimesheetEntry[] → TimesheetEntryRow[] (grouped by unique combo)
//   - Open popup for add/edit, save immediately to API
//   - Submit locks the week (status → SUBMITTED)
//   - Reopen returns to DRAFT

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutSelect, SyutScopedEmployeeSelect } from 'components/ui';
import { SyutTimesheetGrid, SummaryBar } from 'components/ui/SyutTimesheetGrid';
import type { TimesheetEntryRow } from 'components/ui/SyutTimesheetGrid';
import { TimesheetEntryPopup, formatMinutes } from 'components/modals/modules/timesheet/TimesheetEntryPopup';
import { useTimesheetByWeek, useCreateTimesheet, useUpdateTimesheet } from 'hooks/api/useTimesheet';
import TimesheetReopenModal from 'components/modals/modules/timesheet/TimesheetReopenModal';
import { useActivities } from 'hooks/api/useActivity';
import { useEngagements } from 'hooks/api/useEngagement';
import { useChargeTypes } from 'hooks/api/useChargeTypes';
import { useHolidays } from 'hooks/api/useHoliday';
import { useEmployees, useEmployee } from 'hooks/api/useEmployee';
import {
  useTimesheetPolicy,
  dayIndexFromName,
  computeEmployeeFirstWeekStart,
} from 'hooks/api/useConfig';
import { useAuthContext } from 'contexts/AuthContext';
import notificationService from 'services/notificationService';
import type { TimesheetEntry, CreateTimesheetEntryRequest } from 'api/types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TimesheetEntryViewProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** Pre-selected employee (from master screen row click) */
  preSelectedEmployeeId?: string;
  /** Pre-selected week start (from master screen row click) */
  preSelectedWeekStart?: string;
  /** Callback to go back to list view */
  onBackToList?: () => void;
  /**
   * Module action permissions for this user, derived from
   * tbl_role_permissions.module_actions['timesheet-entry']. Controls:
   *   - 'view-all-employees' → unlocks the Employee dropdown for any employee
   *   - 'view-own-team'      → unlocks the Employee dropdown for self + team
   *   - 'reopen'             → shows the Reopen button on submitted timesheets
   * Defaults to ['add','edit','view'] (own-timesheet only) when not provided.
   * Backend (TimesheetsController + TimesheetService) enforces the same scope
   * via ReportScopeGuard — frontend gating is for UX only.
   */
  allowedActions?: string[];
}

// ---------------------------------------------------------------------------
// Week navigation helpers — week start day comes from config (TIMESHEET_POLICY)
// ---------------------------------------------------------------------------

/** Format Date to YYYY-MM-DD without timezone shift */
function toDateStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Parse YYYY-MM-DD to local Date */
function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Snap a date back to the most recent week-start (configurable day-of-week, JS index 0=Sun..6=Sat). */
function getWeekStart(date: Date, weekStartDayJs: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = (d.getDay() - weekStartDayJs + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function getWeekDates(weekStart: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i);
    return toDateStr(d);
  });
}

function formatWeekRange(weekDates: string[]): string {
  if (weekDates.length < 7) return '';
  const start = parseLocalDate(weekDates[0]);
  const end = parseLocalDate(weekDates[6]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${dayNames[start.getDay()]} ${String(start.getDate()).padStart(2, '0')} ${months[start.getMonth()]} - ${dayNames[end.getDay()]} ${String(end.getDate()).padStart(2, '0')} ${months[end.getMonth()]} ${end.getFullYear()}`;
}

// ---------------------------------------------------------------------------
// Transform API entries → grid rows
// ---------------------------------------------------------------------------

/** Build "Client - Type - Year" label from API entry fields */
function buildEngagementLabel(e: TimesheetEntry, fallbackMap: Record<string, string>): string | undefined {
  if (!e.engagementId) return undefined;
  // Prefer fields from API (joined in the backend query)
  if (e.clientName || e.engagementTypeName) {
    return [e.clientName, e.engagementTypeName, e.engagementYear]
      .filter(Boolean)
      .join(' - ');
  }
  // Fallback to engagement list lookup map
  return fallbackMap[e.engagementId] || e.engagementCode || undefined;
}

/** Group flat API entries (one per day per activity) into grid rows (one row per unique combo with dayMinutes map) */
function buildEntryRows(entries: TimesheetEntry[], engagementLabelMap: Record<string, string>): TimesheetEntryRow[] {
  const map = new Map<string, TimesheetEntryRow>();

  entries.forEach(e => {
    const key = `${e.chargeTypeEnumId}|${e.engagementId || ''}|${e.activityId}`;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        chargeTypeKey: e.chargeTypeKey || 'NCNP',
        chargeTypeValue: e.chargeTypeValue || '',
        engagementId: e.engagementId || undefined,
        engagementLabel: buildEngagementLabel(e, engagementLabelMap),
        activityId: e.activityId,
        activityName: e.activityName || '',
        remarks: e.remarks || undefined,
        dayMinutes: {},
      });
    }
    const row = map.get(key)!;
    if (e.workDate) {
      const dateKey = e.workDate.split('T')[0];
      row.dayMinutes[dateKey] = (row.dayMinutes[dateKey] || 0) + e.durationMinutes;
    }
    // Keep latest remarks
    if (e.remarks) row.remarks = e.remarks;
  });

  return Array.from(map.values());
}

/** Flatten grid rows back to API entry format */
function flattenRows(rows: TimesheetEntryRow[], weekDates: string[]): CreateTimesheetEntryRequest[] {
  const result: CreateTimesheetEntryRequest[] = [];
  rows.forEach(row => {
    weekDates.forEach(date => {
      const mins = row.dayMinutes[date] || 0;
      if (mins > 0) {
        result.push({
          workDate: date,
          chargeTypeEnumId: getChargeTypeEnumIdFromRow(row),
          engagementId: row.engagementId || undefined,
          activityId: row.activityId,
          remarks: row.remarks || undefined,
          durationMinutes: mins,
        });
      }
    });
  });
  return result;
}

function getChargeTypeEnumIdFromRow(row: TimesheetEntryRow): string {
  // The row id is: chargeTypeEnumId|engagementId|activityId
  return row.id.split('|')[0];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TimesheetEntryView: React.FC<TimesheetEntryViewProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  preSelectedEmployeeId,
  preSelectedWeekStart,
  onBackToList,
  allowedActions = ['add', 'edit', 'view'],
}) => {
  // --- Auth context ---
  const { user } = useAuthContext();

  // --- Permissions (DB-driven via tbl_role_permissions.module_actions) ---
  const canViewAll = allowedActions.includes('view-all-employees');
  const canViewTeam = allowedActions.includes('view-own-team');
  const canReopen = allowedActions.includes('reopen');

  // --- State ---
  const [currentEmployeeId, setCurrentEmployeeId] = useState<string>(preSelectedEmployeeId || '');

  // --- Config (TIMESHEET_POLICY) — cascades SYSTEM → OFFICE → EMPLOYEE ---
  // We re-fetch when the selected employee or their office changes to apply
  // any office/employee-level overrides admin may have set.
  const { data: selectedEmployee } = useEmployee(currentEmployeeId || null);
  const { config: tsPolicy, refetch: refetchTsPolicy } = useTimesheetPolicy(
    selectedEmployee?.officeId || null,
    currentEmployeeId || null
  );
  const weekStartDayJs = dayIndexFromName(tsPolicy.weekStartDay);

  // Earliest week the employee may log time for: max(implementationStartDate, dateOfJoining)
  // snapped forward to the configured week-start day.
  const earliestAllowedWeekStr = useMemo(() => {
    const dateOfJoining = (selectedEmployee as any)?.dateOfJoining
      ? String((selectedEmployee as any).dateOfJoining).split('T')[0]
      : null;
    return computeEmployeeFirstWeekStart(
      tsPolicy.weekStartDay,
      tsPolicy.implementationStartDate,
      dateOfJoining
    );
  }, [tsPolicy.weekStartDay, tsPolicy.implementationStartDate, selectedEmployee]);

  // --- Week state ---
  const [weekStartDate, setWeekStartDate] = useState<Date>(() => {
    if (preSelectedWeekStart) return parseLocalDate(preSelectedWeekStart);
    return getWeekStart(new Date(), 3); // initial Wed default; corrected on first config load via effect below
  });

  // Realign the active week to the configured week-start day whenever it changes
  // (e.g. user switches office override, or first config fetch lands).
  useEffect(() => {
    setWeekStartDate(prev => {
      const aligned = getWeekStart(prev, weekStartDayJs);
      const earliest = parseLocalDate(earliestAllowedWeekStr);
      // Clamp to earliest if user is somehow before it
      return aligned < earliest ? earliest : aligned;
    });
  }, [weekStartDayJs, earliestAllowedWeekStr]);

  const weekDates = useMemo(() => getWeekDates(weekStartDate), [weekStartDate]);
  const weekStartStr = useMemo(() => toDateStr(weekStartDate), [weekStartDate]);

  // Whether the user can change the Employee selector.
  // Unlocked when the user has either 'view-all-employees' (any employee) or
  // 'view-own-team' (self + reporting tree) on the timesheet-entry module.
  // The backend re-validates the chosen employee on save; frontend gating is UX only.
  // (timesheet_default_employee config controls auto-fill — see effect below.)
  const canPickEmployee = canViewAll || canViewTeam;

  // Popup state
  const [entryPopupOpen, setEntryPopupOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimesheetEntryRow | null>(null);
  const [popupMode, setPopupMode] = useState<'create' | 'edit'>('create');

  // Confirm dialogs
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<TimesheetEntryRow | null>(null);

  // --- Lookups ---
  // The popup uses async server-search for engagements and activities (see
  // SyutEngagementSelect and SyutActivitySelect — both load 500 rows on focus
  // and switch to a server query after the user types 3+ characters). We no
  // longer preload the full master here.
  //
  // engagements / activities arrays below are kept ONLY to pass small seed
  // option lists to those async wrappers so existing entries' saved ids
  // resolve to readable labels in edit mode. The seed page sizes are kept
  // modest because the entry's joined fields (clientName / engagementTypeName /
  // activityName) on the timesheet response usually drive the grid labels
  // already — the seed is a safety net for edge cases.
  const { data: employees } = useEmployees({ pageSize: 500 });
  // selectedEmployee is fetched higher up so its officeId can scope the config lookup
  const { data: engagements } = useEngagements({ pageSize: 200, includeInactive: true });
  const { data: activities } = useActivities({ pageSize: 200, includeInactive: true });
  const { data: chargeTypesRaw } = useChargeTypes();
  const { data: holidaysRaw } = useHolidays({ pageSize: 100 });

  // Auto-select current user's employee if no preSelected
  useEffect(() => {
    if (currentEmployeeId || !user?.userId || employees.length === 0) return;
    const match = employees.find(e => e.userId === user.userId);
    if (match) setCurrentEmployeeId(match.id);
  }, [currentEmployeeId, user, employees]);

  // Sync internal state when the parent supplies a NEW pre-selected row.
  // Without this, tab persistence (display: none) keeps the entry view
  // mounted across visits, so a fresh Edit click would never refresh the
  // employee/week — the user would see the previously-loaded timesheet
  // (e.g. a SUBMITTED row they had reopened earlier in the session).
  useEffect(() => {
    if (preSelectedEmployeeId && preSelectedEmployeeId !== currentEmployeeId) {
      setCurrentEmployeeId(preSelectedEmployeeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedEmployeeId]);

  useEffect(() => {
    if (!preSelectedWeekStart) return;
    const next = parseLocalDate(preSelectedWeekStart);
    setWeekStartDate(prev =>
      toDateStr(prev) === toDateStr(next) ? prev : next,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedWeekStart]);

  // Build lookup options — show role for managers/partners
  const employeeOptions = useMemo(() =>
    employees.map(e => {
      let label = e.name || e.code || e.id;
      const tags: string[] = [];
      if ((e as any).isPartner) tags.push('Partner');
      else if ((e as any).isManager) tags.push('Manager');
      if (tags.length > 0) label = `${label} (${tags.join(', ')})`;
      return { value: e.id, label };
    }),
    [employees]
  );

  // Engagement seed for the popup dropdown — includes ALL engagements (open AND
  // closed). Filtering closed engagements out here would break the edit flow
  // for an entry whose engagement was closed after the entry was saved: the
  // SyutEngagementSelect would fail to resolve its label and the field would
  // appear empty.
  //
  // For NEW entries the SyutEngagementSelect's async loadOptions hits the
  // server with onlyOpen=true (its default), so closed engagements still
  // can't be PICKED for new selections — only resolved as labels for
  // already-referenced ids. Backend guards the save either way.
  const engagementOptions = useMemo(() =>
    engagements.map((e: any) => ({
      value: e.id,
      label: `${e.clientName || ''} - ${e.engagementTypeName || ''} - ${e.year || ''}`.replace(/^ - | - $/g, ''),
    })),
    [engagements]
  );

  // Map engagement ID → "Client - Type - Year" label for grid display
  const engagementLabelMap = useMemo(() =>
    Object.fromEntries(engagements.map((e: any) => [
      e.id,
      `${e.clientName || ''} - ${e.engagementTypeName || ''} - ${e.year || ''}`.replace(/^ - | - $/g, ''),
    ])),
    [engagements]
  );

  const chargeTypeOptions = useMemo(() =>
    (chargeTypesRaw || []).map((ct: any) => ({
      value: ct.id || ct.value,
      label: ct.name || ct.label || ct.value,
      key: ct.key || ct.code || '',
    })),
    [chargeTypesRaw]
  );

  const activityOptionsAll = useMemo(() =>
    activities.map((a: any) => ({
      value: a.id,
      label: `${a.code ? a.code + ' - ' : ''}${a.name || ''}`.trim() || a.id,
      chargeTypeKey: a.chargeTypeKey || '',
      engagementTypeIds: Array.isArray(a.engagementTypeIds) ? a.engagementTypeIds : [],
    })),
    [activities]
  );

  const holidays = useMemo(() =>
    (holidaysRaw || []).map((h: any) => {
      const d = h.holidayDate || h.date || '';
      return d.split('T')[0];
    }).filter((d: string) => weekDates.includes(d)),
    [holidaysRaw, weekDates]
  );

  // Build minimum minutes per day from employee work hours
  // dayOfWeek in DB: 1=Monday..7=Sunday (ISO)
  // JS Date.getDay(): 0=Sunday..6=Saturday
  // Convert JS → ISO: (jsDay === 0) ? 7 : jsDay
  const minimumMinutesPerDay = useMemo(() => {
    const holidaySet = new Set(holidays);
    const workHoursMap = new Map<number, number>(); // ISO dayOfWeek → minimumMinutes
    if (selectedEmployee?.workHours) {
      selectedEmployee.workHours.forEach((wh: any) => {
        workHoursMap.set(wh.dayOfWeek, wh.minimumMinutes || 0);
      });
    }
    const result: Record<string, number> = {};
    weekDates.forEach(date => {
      if (holidaySet.has(date)) {
        result[date] = 0;
        return;
      }
      const d = parseLocalDate(date);
      const jsDay = d.getDay(); // 0=Sun..6=Sat
      const isoDay = jsDay === 0 ? 7 : jsDay; // 1=Mon..7=Sun
      // Use employee work hours if available, otherwise default to config min_daily_minutes
      // for weekdays and 0 for Sat/Sun.
      if (workHoursMap.size > 0) {
        result[date] = workHoursMap.get(isoDay) ?? 0;
      } else {
        result[date] = (isoDay >= 6) ? 0 : tsPolicy.minDailyMinutes; // Sat=6, Sun=7 → 0
      }
    });
    return result;
  }, [weekDates, holidays, selectedEmployee, tsPolicy.minDailyMinutes]);

  // --- Timesheet data ---
  const { data: timesheet, loading: tsLoading, refetch: refetchTimesheet } =
    useTimesheetByWeek(currentEmployeeId || null, weekStartStr || null);

  const { createTimesheet } = useCreateTimesheet();
  const { updateTimesheet } = useUpdateTimesheet();

  const isSubmitted = timesheet?.statusCode === 'SUBMITTED';
  const isReadOnly = isSubmitted;

  // Build entry rows from API data
  const entryRows = useMemo(() => {
    if (!timesheet?.entries) return [];
    return buildEntryRows(timesheet.entries, engagementLabelMap);
  }, [timesheet, engagementLabelMap]);

  // Seeds derived from THIS timesheet's entries — guarantees that whichever
  // engagement / activity an existing entry references can be label-resolved
  // in the popup, even if those master rows are outside the small (pageSize:200)
  // master preload above. The entry response carries the joined fields
  // (clientName / engagementTypeName / activityName / activityCode / etc.) so
  // we synthesize the option labels directly from each entry. Merged with the
  // master-list seed before being passed to the popup.
  const engagementSeedFromEntries = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    (timesheet?.entries || []).forEach((e: any) => {
      const id = e.engagementId;
      if (!id || seen.has(id)) return;
      seen.add(id);
      const base = [e.clientName, e.engagementTypeName, e.engagementYear].filter(Boolean).join(' - ');
      const code = e.engagementCode ? ` (${e.engagementCode})` : '';
      const label = (base + code).trim() || id;
      opts.push({ value: id, label });
    });
    return opts;
  }, [timesheet]);

  const activitySeedFromEntries = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string; chargeTypeKey: string; engagementTypeIds?: string[] }[] = [];
    (timesheet?.entries || []).forEach((e: any) => {
      const id = e.activityId;
      if (!id || seen.has(id)) return;
      seen.add(id);
      const label = `${e.activityCode ? e.activityCode + ' - ' : ''}${e.activityName || ''}`.trim() || id;
      opts.push({
        value: id,
        label,
        chargeTypeKey: e.chargeTypeKey || '',
        engagementTypeIds: [],
      });
    });
    return opts;
  }, [timesheet]);

  // Merged seeds — entry-derived first so the popup's seedOptions lookup hits
  // them before the master-list rows. Dedupe by id; entry-side wins (its label
  // is built from the joined fields on the actual saved entry, which is the
  // most authoritative source for the label of THAT specific id).
  const popupEngagementSeed = useMemo(() => {
    const ids = new Set(engagementSeedFromEntries.map(o => o.value));
    return [
      ...engagementSeedFromEntries,
      ...engagementOptions.filter(o => !ids.has(o.value)),
    ];
  }, [engagementSeedFromEntries, engagementOptions]);

  const popupActivitySeed = useMemo(() => {
    const ids = new Set(activitySeedFromEntries.map(o => o.value));
    return [
      ...activitySeedFromEntries,
      ...activityOptionsAll.filter(o => !ids.has(o.value)),
    ];
  }, [activitySeedFromEntries, activityOptionsAll]);

  // --- Week navigation ---
  const earliestAllowedDate = useMemo(
    () => parseLocalDate(earliestAllowedWeekStr),
    [earliestAllowedWeekStr]
  );

  const isAtEarliestWeek = useMemo(() => {
    return weekStartDate.getTime() <= earliestAllowedDate.getTime();
  }, [weekStartDate, earliestAllowedDate]);

  const handlePrevWeek = useCallback(() => {
    setWeekStartDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d < earliestAllowedDate ? earliestAllowedDate : d;
    });
  }, [earliestAllowedDate]);

  const handleNextWeek = useCallback(() => {
    setWeekStartDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  }, []);

  const handleGoToCurrentWeek = useCallback(() => {
    const current = getWeekStart(new Date(), weekStartDayJs);
    setWeekStartDate(current < earliestAllowedDate ? earliestAllowedDate : current);
  }, [weekStartDayJs, earliestAllowedDate]);

  // --- Entry popup handlers ---
  const handleAddEntry = useCallback(() => {
    setEditingEntry(null);
    setPopupMode('create');
    setEntryPopupOpen(true);
  }, []);

  const handleEditEntry = useCallback((entry: TimesheetEntryRow) => {
    if (entry.isEngagementClosed) {
      // Open in read-only mode
      setEditingEntry(entry);
      setPopupMode('edit');
      setEntryPopupOpen(true);
      return;
    }
    setEditingEntry(entry);
    setPopupMode('edit');
    setEntryPopupOpen(true);
  }, []);

  const handleDeleteEntry = useCallback((entry: TimesheetEntryRow) => {
    if (entry.isEngagementClosed) {
      notificationService.toast.error('Cannot delete \u2014 engagement is closed.');
      return;
    }
    setDeletingEntry(entry);
    setDeleteConfirmOpen(true);
  }, []);

  // --- Save entry (from popup) ---
  const handleSaveEntry = useCallback(async (data: {
    chargeTypeEnumId: string;
    engagementId?: string;
    activityId: string;
    remarks?: string;
    dayMinutes: Record<string, number>;
  }) => {
    try {
      // Build the new/updated row. Look up labels from the merged seeds so
      // optimistic labels are populated when possible; refetchTimesheet()
      // below replaces them with canonical entry-joined labels regardless.
      const newEntryRow: TimesheetEntryRow = {
        id: `${data.chargeTypeEnumId}|${data.engagementId || ''}|${data.activityId}`,
        chargeTypeKey: chargeTypeOptions.find(o => o.value === data.chargeTypeEnumId)?.key || '',
        chargeTypeValue: chargeTypeOptions.find(o => o.value === data.chargeTypeEnumId)?.label || '',
        engagementId: data.engagementId,
        engagementLabel: popupEngagementSeed.find(o => o.value === data.engagementId)?.label,
        activityId: data.activityId,
        activityName: popupActivitySeed.find(o => o.value === data.activityId)?.label || '',
        remarks: data.remarks,
        dayMinutes: data.dayMinutes,
      };

      // Merge with existing rows
      let updatedRows: TimesheetEntryRow[];
      if (popupMode === 'edit' && editingEntry) {
        // Replace existing row
        updatedRows = entryRows.map(row =>
          row.id === editingEntry.id ? newEntryRow : row
        );
        // If the id changed (different combo), remove old and add new
        if (newEntryRow.id !== editingEntry.id) {
          updatedRows = entryRows.filter(row => row.id !== editingEntry.id);
          updatedRows.push(newEntryRow);
        }
      } else {
        // Add new — merge if same combo exists
        const existingIdx = entryRows.findIndex(r => r.id === newEntryRow.id);
        if (existingIdx >= 0) {
          updatedRows = [...entryRows];
          // Merge dayMinutes
          const merged = { ...updatedRows[existingIdx] };
          merged.dayMinutes = { ...merged.dayMinutes };
          Object.entries(data.dayMinutes).forEach(([date, mins]) => {
            merged.dayMinutes[date] = mins;
          });
          merged.remarks = data.remarks || merged.remarks;
          updatedRows[existingIdx] = merged;
        } else {
          updatedRows = [...entryRows, newEntryRow];
        }
      }

      // Flatten and save
      const flatEntries = flattenRows(updatedRows, weekDates);

      if (timesheet) {
        // Update existing timesheet
        await updateTimesheet(timesheet.id, {
          employeeId: currentEmployeeId,
          weekStartDate: weekStartStr,
          entries: flatEntries,
          statusCode: timesheet.statusCode,
          rowVersion: timesheet.rowVersion,
        });
      } else {
        // Create new timesheet
        await createTimesheet({
          employeeId: currentEmployeeId,
          weekStartDate: weekStartStr,
          entries: flatEntries,
          statusCode: 'DRAFT',
        });
      }

      setEntryPopupOpen(false);
      setEditingEntry(null);
      refetchTimesheet();
      notificationService.toast.success(popupMode === 'create' ? 'Entry added.' : 'Entry updated.');
    } catch (err: any) {
      const apiBody = err?.response?.data;
      let msg = 'Failed to save entry. Please try again.';
      if (typeof apiBody?.message === 'string' && apiBody.message) msg = apiBody.message;
      else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) msg = apiBody.errors.join(', ');
      else if (apiBody?.errors && typeof apiBody.errors === 'object') msg = Object.values(apiBody.errors).flat().join(', ');
      else if (err?.message) msg = err.message;
      console.error('Timesheet save error:', err?.response?.status, apiBody || err);
      notificationService.toast.error(msg, 6000);
    }
  }, [
    chargeTypeOptions, popupEngagementSeed, popupActivitySeed,
    popupMode, editingEntry, entryRows, weekDates,
    timesheet, currentEmployeeId, weekStartStr,
    createTimesheet, updateTimesheet, refetchTimesheet,
  ]);

  // --- Delete entry (confirmed) ---
  const handleConfirmDelete = useCallback(async () => {
    if (!deletingEntry || !timesheet) return;
    try {
      const updatedRows = entryRows.filter(row => row.id !== deletingEntry.id);
      const flatEntries = flattenRows(updatedRows, weekDates);
      await updateTimesheet(timesheet.id, {
        employeeId: currentEmployeeId,
        weekStartDate: weekStartStr,
        entries: flatEntries,
        statusCode: timesheet.statusCode,
        rowVersion: timesheet.rowVersion,
      });
      setDeleteConfirmOpen(false);
      setDeletingEntry(null);
      refetchTimesheet();
      notificationService.toast.success('Entry deleted.');
    } catch {
      notificationService.toast.error('Failed to delete entry.');
    }
  }, [deletingEntry, timesheet, entryRows, weekDates, currentEmployeeId, weekStartStr, updateTimesheet, refetchTimesheet]);

  // --- Submit ---
  const handleSubmitClick = useCallback(() => {
    if (entryRows.length === 0) {
      notificationService.toast.warning('No entries to submit.');
      return;
    }
    // Check weekly total vs minimum
    const weeklyTotal = weekDates.reduce((sum, date) =>
      sum + entryRows.reduce((s, e) => s + (e.dayMinutes[date] || 0), 0), 0
    );
    const weeklyMin = Object.values(minimumMinutesPerDay).reduce((s, v) => s + v, 0);

    if (weeklyTotal < weeklyMin) {
      // Show warning but allow submit via confirmation
      setSubmitConfirmOpen(true);
    } else {
      // Sufficient hours — submit directly
      handleSubmit();
    }
  }, [entryRows, weekDates, minimumMinutesPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(async () => {
    if (!timesheet) return;
    try {
      const flatEntries = flattenRows(entryRows, weekDates);
      await updateTimesheet(timesheet.id, {
        employeeId: currentEmployeeId,
        weekStartDate: weekStartStr,
        entries: flatEntries,
        submittedAt: new Date().toISOString(),
        statusCode: 'SUBMITTED',
        rowVersion: timesheet.rowVersion,
      });
      setSubmitConfirmOpen(false);
      refetchTimesheet();
      notificationService.toast.success('Timesheet submitted successfully.');
    } catch {
      notificationService.toast.error('Failed to submit timesheet.');
    }
  }, [timesheet, entryRows, weekDates, currentEmployeeId, weekStartStr, updateTimesheet, refetchTimesheet]);

  // --- Reopen ---
  // Opens the dedicated reopen modal so the user enters a mandatory reason
  // (captured into reopened_at/by/by_name/reason on tbl_timesheet for audit).
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const handleReopen = useCallback(() => {
    if (!timesheet) return;
    setReopenModalOpen(true);
  }, [timesheet]);

  // --- Weekly totals for submit validation ---
  const weekTotal = useMemo(() =>
    weekDates.reduce((sum, date) =>
      sum + entryRows.reduce((s, e) => s + (e.dayMinutes[date] || 0), 0), 0
    ), [weekDates, entryRows]);
  const weekMin = useMemo(() =>
    Object.values(minimumMinutesPerDay).reduce((s, v) => s + v, 0),
    [minimumMinutesPerDay]);

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const btns: ActionButton[] = [];
    if (onBackToList) {
      btns.push({
        id: 'back',
        icon: <IconSystem.chevronLeft size={16} />,
        label: 'Back to List',
        onClick: onBackToList,
        variant: 'secondary',
      });
    }
    if (!isSubmitted) {
      btns.push({
        id: 'add-entry',
        icon: <IconSystem.add size={16} />,
        label: 'Add Entry',
        onClick: handleAddEntry,
        variant: 'primary',
      });
      if (timesheet && entryRows.length > 0) {
        btns.push({
          id: 'submit',
          icon: <IconSystem.check size={16} />,
          label: 'Submit',
          onClick: handleSubmitClick,
          variant: 'primary',
        });
      }
    } else if (canReopen) {
      // Only users with the 'reopen' permission see the Reopen button on submitted timesheets.
      btns.push({
        id: 'reopen',
        icon: <IconSystem.refresh size={16} />,
        label: 'Reopen',
        onClick: handleReopen,
        variant: 'secondary',
      });
    }
    return btns;
  }, [isSubmitted, canReopen, timesheet, entryRows.length, handleAddEntry, handleSubmitClick, handleReopen, onBackToList]);

  // --- Build popup initialData for edit ---
  const popupInitialData = useMemo(() => {
    if (!editingEntry) return null;
    return {
      chargeTypeEnumId: getChargeTypeEnumIdFromRow(editingEntry),
      engagementId: editingEntry.engagementId,
      activityId: editingEntry.activityId,
      remarks: editingEntry.remarks,
      dayMinutes: editingEntry.dayMinutes,
    };
  }, [editingEntry]);

  // --- Render ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Timesheet Entry"
        description="Weekly timesheet entry and submission."
        icon={<IconSystem.clock size={18} />}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* ── Sticky top: Employee bar + Summary bar ── */}
            <div style={{ flexShrink: 0 }}>
              {/* Employee + Week navigation bar */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.375rem 0.75rem',
                background: 'var(--bg-secondary, #f8fafc)',
                borderRadius: '8px 8px 0 0',
                border: '1px solid var(--border-color)',
                borderBottom: 'none',
                flexWrap: 'wrap',
              }}>
                {/* Employee selector — server-scoped to manage-* on timesheet-entry.
                    The dropdown only lists employees the caller is authorized to act
                    upon. Locked to the logged-in user when no manage-* grant exists. */}
                <div style={{ minWidth: '200px', maxWidth: '280px' }}>
                  <SyutScopedEmployeeSelect
                    label="Employee"
                    scopeMode="action"
                    moduleId="timesheet-entry"
                    value={currentEmployeeId || undefined}
                    seedOptions={employeeOptions}
                    onChange={(val) => setCurrentEmployeeId(val || '')}
                    placeholder="Select employee..."
                    disabled={!canPickEmployee}
                  />
                </div>

                {/* Week navigator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, justifyContent: 'center' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={handlePrevWeek}
                    disabled={isAtEarliestWeek}
                    title={isAtEarliestWeek
                      ? `Earliest allowed week: ${earliestAllowedWeekStr} (employee start date / system implementation start)`
                      : 'Previous week'}
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
                  >
                    <IconSystem.chevronLeft size={16} />
                  </button>
                  <div style={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    minWidth: '240px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                    onClick={handleGoToCurrentWeek}
                    title="Go to current week"
                  >
                    {formatWeekRange(weekDates)}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={handleNextWeek}
                    title="Next week"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
                  >
                    <IconSystem.chevronRight size={16} />
                  </button>
                </div>

                {/* Status badge */}
                {timesheet && (
                  <span className={`badge badge-${isSubmitted ? 'info' : 'inactive'}`}>
                    {timesheet.statusCode === 'SUBMITTED' ? 'Submitted' : 'Draft'}
                  </span>
                )}
              </div>

              {/* Summary bar — day totals, min, diff (pinned below employee bar) */}
              {currentEmployeeId && (
                <SummaryBar
                  weekDates={weekDates}
                  entries={entryRows}
                  holidays={holidays}
                  minimumMinutesPerDay={minimumMinutesPerDay}
                />
              )}
            </div>

            {/* Submitted-state info banner — explains why the grid is read-only
                and gives the path forward when the caller has the 'reopen'
                module action. Renders only when status = SUBMITTED. */}
            {isSubmitted && (
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.55rem 0.9rem',
                  background: 'rgba(59,130,246,0.08)',
                  borderTop: '1px solid var(--border-color)',
                  borderBottom: '1px solid var(--border-color)',
                  fontSize: '0.82rem',
                  color: 'var(--text-secondary)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <IconSystem.lock size={14} />
                  <span>
                    This week is <strong>submitted</strong> and read-only.
                    {canReopen ? ' Reopen to edit entries.' : ' Ask your manager to reopen if changes are needed.'}
                  </span>
                </div>
                {canReopen && (
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}
                    onClick={handleReopen}
                  >
                    <IconSystem.refresh size={14} /> Reopen
                  </button>
                )}
              </div>
            )}

            {/* ── Scrollable area: grid content ── */}
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {/* Loading indicator */}
              {tsLoading && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Loading timesheet...
                </div>
              )}

              {/* No employee selected */}
              {!currentEmployeeId && !tsLoading && (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0 0 8px 8px',
                }}>
                  Please select an employee to view or enter their timesheet.
                </div>
              )}

              {/* Timesheet grid */}
              {currentEmployeeId && !tsLoading && (
                <SyutTimesheetGrid
                  weekDates={weekDates}
                  entries={entryRows}
                  holidays={holidays}
                  minimumMinutesPerDay={minimumMinutesPerDay}
                  readOnly={isReadOnly}
                  onEdit={handleEditEntry}
                  onDelete={handleDeleteEntry}
                />
              )}
            </div>

            {/* ── Footer: weekly total ── */}
            <div style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.3rem 0.75rem',
              background: 'var(--bg-secondary, #f8fafc)',
              borderRadius: '0 0 8px 8px',
              border: '1px solid var(--border-color)',
              borderTop: '2px solid var(--border-color)',
              fontSize: '0.8125rem',
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                {entryRows.length} {entryRows.length === 1 ? 'entry' : 'entries'}
              </span>
              <span style={{ fontWeight: 700 }}>
                Weekly Total: {formatMinutes(weekTotal)} / {formatMinutes(weekMin)}
                {weekTotal < weekMin && weekMin > 0 && (
                  <span style={{ color: '#dc2626', marginLeft: '0.5rem', fontWeight: 600, fontSize: '0.75rem' }}>
                    ({formatMinutes(weekMin - weekTotal)} under)
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Entry popup */}
      <TimesheetEntryPopup
        isOpen={entryPopupOpen}
        mode={popupMode}
        initialData={popupInitialData}
        weekDates={weekDates}
        holidays={holidays}
        chargeTypeOptions={chargeTypeOptions}
        engagementOptions={popupEngagementSeed}
        activityOptions={popupActivitySeed}
        readOnly={editingEntry?.isEngagementClosed || false}
        onSave={handleSaveEntry}
        onClose={() => { setEntryPopupOpen(false); setEditingEntry(null); }}
      />

      {/* Submit confirmation (when under minimum hours) */}
      <ConfirmDialog
        open={submitConfirmOpen}
        title="Submit Timesheet?"
        message={`Your total hours (${formatMinutes(weekTotal)}) are below the minimum (${formatMinutes(weekMin)}). Submit anyway?`}
        confirmLabel="Submit"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleSubmit}
        onCancel={() => setSubmitConfirmOpen(false)}
      />

      {/* Delete entry confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Entry?"
        message={`Are you sure you want to remove "${deletingEntry?.engagementLabel || deletingEntry?.activityName || 'this entry'}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteConfirmOpen(false); setDeletingEntry(null); }}
      />

      {/* Reopen Timesheet Modal — opened from the in-context Reopen button. */}
      <TimesheetReopenModal
        isOpen={reopenModalOpen}
        timesheet={timesheet || null}
        onClose={() => setReopenModalOpen(false)}
        onReopened={() => { refetchTimesheet(); }}
      />
    </div>
  );
};

export default TimesheetEntryView;
